import { useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';

import { BottomSheet } from './BottomSheet';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  currentDate: Date;
  onSelectMonth: (date: Date) => void;
  buttonRef?: RefObject<HTMLButtonElement>;
};

const months = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

export const MonthPickerModal = ({
  isOpen,
  onClose,
  currentDate,
  onSelectMonth,
  buttonRef,
}: Props) => {
  const [viewYear, setViewYear] = useState(currentDate.getFullYear());
  const [draftMonth, setDraftMonth] = useState(currentDate.getMonth());
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 768px)').matches;
  });

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }

    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();

  const handleMonthClick = (monthIndex: number) => {
    setDraftMonth(monthIndex);
  };

  const handlePrevYear = () => {
    setViewYear(viewYear - 1);
  };

  const handleNextYear = () => {
    setViewYear(viewYear + 1);
  };

  const getPosition = () => {
    if (!buttonRef?.current) {
      return { top: '60px', left: '200px' };
    }
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
    };
  };

  const getMonthStyle = (monthIndex: number) => {
    const isCurrentMonth =
      viewYear === currentYear && monthIndex === currentMonth;
    const monthDate = new Date(viewYear, monthIndex, 1);
    const todayDate = new Date(todayYear, todayMonth, 1);
    const nextMonthDate = new Date(todayYear, todayMonth + 1, 1);

    if (isCurrentMonth) {
      return {
        containerClass: 'bg-[#151515]',
        textClass: 'text-white',
        opacity: '',
        mt: 'mt-[-1.00px]',
      };
    }
    if (monthDate.getTime() === nextMonthDate.getTime()) {
      return {
        containerClass:
          'mt-[-1.00px] mb-[-1.00px] border border-solid border-[#151515]',
        textClass: 'text-[#151515]',
        opacity: '',
        mt: 'mt-[-0.50px]',
      };
    }
    if (monthDate < todayDate) {
      return {
        containerClass: '',
        textClass: 'text-[#151515]',
        opacity: 'opacity-50',
        mt: 'mt-[-1.00px]',
      };
    }
    return {
      containerClass: 'bg-[#f1f7fa]',
      textClass: 'text-[#151515]',
      opacity: '',
      mt: 'mt-[-1.00px]',
    };
  };

  const position = getPosition();

  const desktopPanel = (
    <div
      className="absolute hidden w-80 flex-col items-start gap-4 rounded-3xl bg-white p-5 shadow-[0px_2px_16px_#1515151f] md:flex"
      style={{ top: position.top, left: position.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative flex w-full flex-[0_0_auto] flex-col items-start gap-3 self-stretch">
        <div className="relative flex w-full flex-[0_0_auto] items-center justify-between self-stretch">
          <div
            onClick={handlePrevYear}
            className="relative inline-flex flex-[0_0_auto] cursor-pointer items-center gap-2.5 rounded-[100px] bg-[#f1f7fa] p-1.5"
          >
            <div className="relative flex size-5 items-center justify-center">
              <svg
                width="6"
                height="12"
                viewBox="0 0 6 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.45215 1.28014C5.74504 0.987243 5.74504 0.512483 5.45215 0.219589C5.15924 -0.0731421 4.68444 -0.0732498 4.3916 0.219589L1.6543 2.95689C1.26706 3.34413 0.944099 3.6662 0.703125 3.95006C0.456544 4.24054 0.251713 4.54038 0.134766 4.90025C-0.0445833 5.45238 -0.0445433 6.04733 0.134766 6.59947C0.251686 6.95931 0.456591 7.25921 0.703125 7.54967C0.944069 7.83349 1.26712 8.15566 1.6543 8.54283L4.3916 11.2801C4.68449 11.573 5.15925 11.573 5.45215 11.2801C5.74504 10.9872 5.74504 10.5125 5.45215 10.2196L2.71484 7.48228C2.31019 7.07763 2.03877 6.80524 1.84668 6.57896C1.66071 6.35985 1.59298 6.23315 1.56152 6.13658C1.48001 5.88558 1.47997 5.61414 1.56152 5.36314C1.593 5.26656 1.66065 5.13992 1.84668 4.92076C2.03879 4.69446 2.31011 4.42218 2.71484 4.01744L5.45215 1.28014Z"
                  fill="#151515"
                />
              </svg>
            </div>
          </div>
          <div className="relative flex-1 text-center text-[1.25rem] font-medium leading-7 tracking-normal text-[#151515]">
            {viewYear}
          </div>
          <div
            onClick={handleNextYear}
            className="relative inline-flex flex-[0_0_auto] cursor-pointer items-center gap-2.5 rounded-[100px] bg-[#f1f7fa] p-1.5"
          >
            <div className="relative flex size-5 rotate-180 items-center justify-center">
              <svg
                width="6"
                height="12"
                viewBox="0 0 6 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.45215 1.28014C5.74504 0.987243 5.74504 0.512483 5.45215 0.219589C5.15924 -0.0731421 4.68444 -0.0732498 4.3916 0.219589L1.6543 2.95689C1.26706 3.34413 0.944099 3.6662 0.703125 3.95006C0.456544 4.24054 0.251713 4.54038 0.134766 4.90025C-0.0445833 5.45238 -0.0445433 6.04733 0.134766 6.59947C0.251686 6.95931 0.456591 7.25921 0.703125 7.54967C0.944069 7.83349 1.26712 8.15566 1.6543 8.54283L4.3916 11.2801C4.68449 11.573 5.15925 11.573 5.45215 11.2801C5.74504 10.9872 5.74504 10.5125 5.45215 10.2196L2.71484 7.48228C2.31019 7.07763 2.03877 6.80524 1.84668 6.57896C1.66071 6.35985 1.59298 6.23315 1.56152 6.13658C1.48001 5.88558 1.47997 5.61414 1.56152 5.36314C1.593 5.26656 1.66065 5.13992 1.84668 4.92076C2.03879 4.69446 2.31011 4.42218 2.71484 4.01744L5.45215 1.28014Z"
                  fill="#151515"
                />
              </svg>
            </div>
          </div>
        </div>
        <div className="relative flex w-full flex-[0_0_auto] flex-col items-start gap-2 self-stretch">
          <div className="relative flex w-full flex-[0_0_auto] items-start gap-2 self-stretch">
            {months.slice(0, 3).map((month, index) => {
              const style = getMonthStyle(index);
              const isSelected = draftMonth === index;
              return (
                <div
                  key={index}
                  onClick={() => handleMonthClick(index)}
                  className={`relative flex h-[36px] flex-1 grow cursor-pointer items-center gap-2 rounded-[12px] p-[8px] ${
                    isSelected ? 'bg-[#151515]' : style.containerClass
                  }`}
                >
                  <div
                    className={`relative ${
                      style.mt
                    } flex-1 text-center text-[0.875rem] font-normal leading-5 tracking-normal ${
                      isSelected ? 'text-white' : style.textClass
                    } ${style.opacity}`}
                  >
                    {month}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="relative flex w-full flex-[0_0_auto] items-start gap-2 self-stretch">
            {months.slice(3, 6).map((month, index) => {
              const monthIndex = index + 3;
              const style = getMonthStyle(monthIndex);
              const isSelected = draftMonth === monthIndex;
              return (
                <div
                  key={monthIndex}
                  onClick={() => handleMonthClick(monthIndex)}
                  className={`relative flex h-[36px] flex-1 grow cursor-pointer items-center gap-2 rounded-[12px] p-[8px] ${
                    isSelected ? 'bg-[#151515]' : style.containerClass
                  }`}
                >
                  <div
                    className={`relative ${
                      style.mt
                    } flex-1 text-center text-[0.875rem] font-normal leading-5 tracking-normal ${
                      isSelected ? 'text-white' : style.textClass
                    } ${style.opacity}`}
                  >
                    {month}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="relative flex w-full flex-[0_0_auto] items-start gap-2 self-stretch">
            {months.slice(6, 9).map((month, index) => {
              const monthIndex = index + 6;
              const style = getMonthStyle(monthIndex);
              const isSelected = draftMonth === monthIndex;
              return (
                <div
                  key={monthIndex}
                  onClick={() => handleMonthClick(monthIndex)}
                  className={`relative flex h-[36px] flex-1 grow cursor-pointer items-center gap-2 rounded-[12px] p-[8px] ${
                    isSelected ? 'bg-[#151515]' : style.containerClass
                  }`}
                >
                  <div
                    className={`relative ${
                      style.mt
                    } flex-1 text-center text-[0.875rem] font-normal leading-5 tracking-normal ${
                      isSelected ? 'text-white' : style.textClass
                    } ${style.opacity}`}
                  >
                    {month}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="relative flex w-full flex-[0_0_auto] items-start gap-2 self-stretch">
            {months.slice(9, 12).map((month, index) => {
              const monthIndex = index + 9;
              const style = getMonthStyle(monthIndex);
              const isSelected = draftMonth === monthIndex;
              return (
                <div
                  key={monthIndex}
                  onClick={() => handleMonthClick(monthIndex)}
                  className={`relative flex h-[36px] flex-1 grow cursor-pointer items-center gap-2 rounded-[12px] p-[8px] ${
                    isSelected ? 'bg-[#151515]' : style.containerClass
                  }`}
                >
                  <div
                    className={`relative ${
                      style.mt
                    } flex-1 text-center text-[0.875rem] font-normal leading-5 tracking-normal ${
                      isSelected ? 'text-white' : style.textClass
                    } ${style.opacity}`}
                  >
                    {month}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="mt-[8px] w-full rounded-[12px] bg-[#0788f1] px-[16px] py-[12px] text-[1rem] font-bold leading-5 text-white"
        onClick={() => {
          const newDate = new Date(viewYear, draftMonth, 1);
          onSelectMonth(newDate);
          onClose();
        }}
      >
        Применить
      </button>
    </div>
  );

  const mobileContent = useMemo(() => {
    return (
      <div>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handlePrevYear}
            className="size-48px] inline-flex items-center gap-2.5 rounded-[100px] bg-[#f1f7fa] p-[14px]"
          >
            <div className="flex size-5 items-center justify-center">
              <svg
                width="6"
                height="12"
                viewBox="0 0 6 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.45215 1.28014C5.74504 0.987243 5.74504 0.512483 5.45215 0.219589C5.15924 -0.0731421 4.68444 -0.0732498 4.3916 0.219589L1.6543 2.95689C1.26706 3.34413 0.944099 3.6662 0.703125 3.95006C0.456544 4.24054 0.251713 4.54038 0.134766 4.90025C-0.0445833 5.45238 -0.0445433 6.04733 0.134766 6.59947C0.251686 6.95931 0.456591 7.25921 0.703125 7.54967C0.944069 7.83349 1.26712 8.15566 1.6543 8.54283L4.3916 11.2801C4.68449 11.573 5.15925 11.573 5.45215 11.2801C5.74504 10.9872 5.74504 10.5125 5.45215 10.2196L2.71484 7.48228C2.31019 7.07763 2.03877 6.80524 1.84668 6.57896C1.66071 6.35985 1.59298 6.23315 1.56152 6.13658C1.48001 5.88558 1.47997 5.61414 1.56152 5.36314C1.593 5.26656 1.66065 5.13992 1.84668 4.92076C2.03879 4.69446 2.31011 4.42218 2.71484 4.01744L5.45215 1.28014Z"
                  fill="#151515"
                />
              </svg>
            </div>
          </button>
          <div className="text-[24px] font-medium leading-7 text-[#151515]">
            {viewYear}
          </div>
          <button
            type="button"
            onClick={handleNextYear}
            className="size-48px inline-flex items-center gap-2.5 rounded-[100px] bg-[#f1f7fa] p-[14px]"
          >
            <div className="flex size-5 rotate-180 items-center justify-center">
              <svg
                width="6"
                height="12"
                viewBox="0 0 6 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.45215 1.28014C5.74504 0.987243 5.74504 0.512483 5.45215 0.219589C5.15924 -0.0731421 4.68444 -0.0732498 4.3916 0.219589L1.6543 2.95689C1.26706 3.34413 0.944099 3.6662 0.703125 3.95006C0.456544 4.24054 0.251713 4.54038 0.134766 4.90025C-0.0445833 5.45238 -0.0445433 6.04733 0.134766 6.59947C0.251686 6.95931 0.456591 7.25921 0.703125 7.54967C0.944069 7.83349 1.26712 8.15566 1.6543 8.54283L4.3916 11.2801C4.68449 11.573 5.15925 11.573 5.45215 11.2801C5.74504 10.9872 5.74504 10.5125 5.45215 10.2196L2.71484 7.48228C2.31019 7.07763 2.03877 6.80524 1.84668 6.57896C1.66071 6.35985 1.59298 6.23315 1.56152 6.13658C1.48001 5.88558 1.47997 5.61414 1.56152 5.36314C1.593 5.26656 1.66065 5.13992 1.84668 4.92076C2.03879 4.69446 2.31011 4.42218 2.71484 4.01744L5.45215 1.28014Z"
                  fill="#151515"
                />
              </svg>
            </div>
          </button>
        </div>

        <div className="mt-[16px] flex flex-col gap-[8px]">
          {[0, 3, 6, 9].map((rowStart) => (
            <div key={rowStart} className="flex w-full items-start gap-2">
              {months.slice(rowStart, rowStart + 3).map((month, idx) => {
                const monthIndex = rowStart + idx;
                const style = getMonthStyle(monthIndex);
                const isSelected = draftMonth === monthIndex;
                return (
                  <button
                    key={monthIndex}
                    type="button"
                    onClick={() => handleMonthClick(monthIndex)}
                    className={`flex flex-1 items-center justify-center rounded-[12px] p-[14px_8px] ${
                      isSelected
                        ? 'bg-[#151515]'
                        : style.containerClass || 'bg-[#f1f7fa]'
                    }`}
                  >
                    <div
                      className={`text-center text-[0.875rem] font-normal leading-5 ${
                        isSelected ? 'text-white' : style.textClass
                      } ${style.opacity}`}
                    >
                      {month}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="mt-[32px] flex w-full gap-[12px]">
          <button
            type="button"
            onClick={() => {
              setDraftMonth(currentDate.getMonth());
              setViewYear(currentDate.getFullYear());
            }}
            className="flex flex-1 items-center justify-center rounded-[12px] bg-[#f1f7fa] p-[20px] text-[16px] font-bold leading-5 text-[#151515]"
          >
            Сбросить
          </button>
          <button
            type="button"
            onClick={() => {
              const newDate = new Date(viewYear, draftMonth, 1);
              onSelectMonth(newDate);
              onClose();
            }}
            className="flex flex-1 items-center justify-center rounded-[12px] bg-[#0788f1] p-[20px] text-[16px] font-bold leading-5 text-white"
          >
            Применить
          </button>
        </div>
      </div>
    );
  }, [
    currentDate,
    draftMonth,
    handleNextYear,
    handlePrevYear,
    onClose,
    onSelectMonth,
    viewYear,
  ]);

  useEffect(() => {
    if (isOpen) {
      setViewYear(currentDate.getFullYear());
      setDraftMonth(currentDate.getMonth());
    }
  }, [currentDate, isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {isDesktop ? (
        <div className="fixed inset-0 z-50" onClick={onClose}>
          {desktopPanel}
        </div>
      ) : (
        <BottomSheet isOpen={isOpen} onClose={onClose} title="Календарь">
          {mobileContent}
        </BottomSheet>
      )}
    </>
  );
};
